import { ChevronDown, Loader2, RefreshCw, Unplug } from "lucide-react";
import { type FC, useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { ListRow } from "@/components/ui/list-card";
import { StatusBadge } from "@/components/ui/status-badge";
import { cn } from "@/lib/utils";

import type { Connector } from "../types";
import { ConnectorLogo } from "./ConnectorLogo";
import { STATUS_LABEL } from "./ConnectorStatusBadge";

type Props = {
  connector: Connector;
  /** The Connect Link for this app is open in another tab. */
  connecting: boolean;
  disconnecting: boolean;
  onConnect: (slug: string) => void;
  onDisconnect: (slug: string) => void;
};

/**
 * One app as a settings row: logo, name, what the agents can do with it and
 * the one action that makes sense for its status. Connected apps get a
 * success badge and Manage, whose menu holds Reconnect and Disconnect.
 * Disconnecting asks first: the agents lose the account immediately.
 */
export const ConnectorCard: FC<Props> = ({
  connector,
  connecting,
  disconnecting,
  onConnect,
  onDisconnect
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { slug, name, logo, description, status } = connector;
  const canManage = status === "connected" || status === "error";
  const canConnect =
    status === "not_connected" || status === "pending" || status === "error";
  const disabled = status === "unavailable";

  const statusText = connecting
    ? "Connecting…"
    : status === "pending"
    ? STATUS_LABEL[status]
    : null;

  return (
    <ListRow
      media={<ConnectorLogo name={name} src={logo} />}
      title={
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate font-medium">{name}</span>
          {status === "connected" && !connecting ? (
            <StatusBadge tone="success" dot data-testid="connector-connected">
              {STATUS_LABEL.connected}
            </StatusBadge>
          ) : null}
          {status === "error" && !connecting ? (
            <StatusBadge tone="danger" dot>
              {STATUS_LABEL.error}
            </StatusBadge>
          ) : null}
        </span>
      }
      description={
        <span className="line-clamp-2 text-sm text-fg-3">{description}</span>
      }
      className={cn(disabled && "opacity-60")}
      data-testid={`connector-card-${slug}`}
      data-status={connecting ? "connecting" : status}
      control={
        <>
          {statusText ? (
            <span
              className="text-sm whitespace-nowrap text-fg-3"
              data-testid="connector-status"
            >
              {statusText}
            </span>
          ) : null}
          {canConnect ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={connecting || disconnecting}
              onClick={() => onConnect(slug)}
              data-testid={`connector-connect-${slug}`}
            >
              {connecting ? <Loader2 className="animate-spin" /> : null}
              {status === "error"
                ? "Reconnect"
                : connecting
                ? "Waiting…"
                : "Connect"}
            </Button>
          ) : null}
          {canManage ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  disabled={disconnecting || connecting}
                  data-testid={`connector-manage-${slug}`}
                >
                  {disconnecting || connecting ? (
                    <Loader2 className="animate-spin" />
                  ) : null}
                  Manage
                  <ChevronDown className="text-fg-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                {status === "connected" ? (
                  <DropdownMenuItem
                    onSelect={() => onConnect(slug)}
                    data-testid={`connector-reconnect-${slug}`}
                  >
                    <RefreshCw />
                    Reconnect
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={() => setConfirmOpen(true)}
                  data-testid={`connector-disconnect-${slug}`}
                >
                  <Unplug />
                  Disconnect
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
          {disabled ? (
            <Button size="sm" variant="secondary" disabled>
              Connect
            </Button>
          ) : null}
          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Disconnect {name}?</AlertDialogTitle>
                <AlertDialogDescription>
                  Your agents will lose access to {name} right away. You can
                  connect it again at any time.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep Connected</AlertDialogCancel>
                <AlertDialogAction
                  variant="destructive"
                  onClick={() => {
                    setConfirmOpen(false);
                    onDisconnect(slug);
                  }}
                  data-testid={`connector-disconnect-confirm-${slug}`}
                >
                  Disconnect
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      }
    />
  );
};
